// CompareCellChip - Vertical cell chip for compare sidebar
import { h } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { getCellFromExplorerCache, getCellNormalized, getSignedPictureUrl } from '../../modules/dataService.js'
import { basis } from '../../state/globalSignals.js'
import { filteredCells } from '../../state/globalSignals.js'
import { toggleCellVisibility, isCellHidden } from '../../state/filterStore.js'
import { getSlugFromProductId } from '../../utils/cellSlugMap.js'
import { isCellLockedAtLimit, openLimitModalOrToast } from '../../state/demoLimitStore.js'

const LockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
)

// Icons
const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
)

const EyeOffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
    <line x1="1" y1="1" x2="23" y2="23"></line>
  </svg>
)

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
)

export default function CompareCellChip({ cellId, index, onRemove }) {
  const [, setLocation] = useLocation()
  const [cellData, setCellData] = useState(null)
  const [imageUrl, setImageUrl] = useState(null)
  const [imageError, setImageError] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    setImageError(false)
    // Try explorer cache first (zero API calls), fall back to individual fetch
    const cached = getCellFromExplorerCache(cellId)
    const dataPromise = cached
      ? Promise.resolve(cached)
      : getCellNormalized(cellId, { fieldGating: 'demo' }) // Compare = browse surface: server-gate premium for demo
    dataPromise
      .then(data => {
        setCellData(data)
        setLoading(false)
        // Prefer the backend-gated thumbnail_url — it's only set when a 3D render
        // actually exists (like the grid). Cells without a render then degrade to
        // the placeholder instead of the signed-urls path, which returns a URL
        // unconditionally and 404s on the byte fetch → broken <img>.
        const gatedThumb = getCellFromExplorerCache(cellId)?.thumbnail_url
        if (gatedThumb) {
          setImageUrl(gatedThumb)
          return null
        }
        return getSignedPictureUrl(cellId, 'website_picture_cell1.png')
      })
      .then(signedUrl => { if (signedUrl) setImageUrl(signedUrl) })
      .catch(err => {
        console.error('[CompareCellChip] Error loading cell:', cellId, err)
        setLoading(false)
      })
  }, [cellId])
  
  if (loading) {
    return (
      <div className="compare-cell-chip loading">
        <div className="chip-index">{index + 1}</div>
        <div className="chip-content">
          <div className="chip-name">Loading...</div>
        </div>
      </div>
    )
  }
  
  if (!cellData) {
    return (
      <div className="compare-cell-chip error">
        <div className="chip-index">{index + 1}</div>
        <div className="chip-content">
          <div className="chip-name">Error loading cell</div>
        </div>
        <button className="chip-remove" onClick={onRemove}>×</button>
      </div>
    )
  }
  
  // Get energy value based on current basis
  const currentBasis = basis.value
  let energyValue = 0
  let energyUnit = ''
  
  if (currentBasis === 'abs') {
    energyValue = cellData.electrical?.E_c10_Wh || 0
    energyUnit = 'Wh'
  } else if (currentBasis === 'grav') {
    energyValue = cellData.electrical?.E_grav_Wh_per_kg || 0
    energyUnit = 'Wh/kg'
  } else if (currentBasis === 'vol') {
    energyValue = cellData.electrical?.E_vol_Wh_per_L || 0
    energyUnit = 'Wh/L'
  }
  
  // Check if cell is hidden from plots
  const isHidden = isCellHidden(cellId)
  
  const manufacturer = cellData.general?.manufacturer || cellData.manufacturer || 'Unknown'
  const name = cellData.general?.name || cellData.name || 'Unknown'
  const chemistry = cellData.chemical?.main_chemistry || cellData.chemical?.cathode || 'N/A'
  const format = cellData.mechanical?.format_group || cellData.mechanical?.format || 'N/A'
  
  const handleToggleVisibility = (e) => {
    e.stopPropagation()
    toggleCellVisibility(cellId)
  }
  
  const handleRemove = (e) => {
    e.stopPropagation()
    onRemove(e)
  }
  
  // Bug B: DEMO users at today's deep-dive limit — cells not opened yet
  // today show a lock affordance; the chip itself stays in the cart
  // (cart presence is allowed regardless of the limit, see WS 1.4).
  const limitLocked = isCellLockedAtLimit(cellId)

  const handleCellClick = () => {
    if (limitLocked) {
      openLimitModalOrToast({})
      return
    }
    // Get slug from cellId using the slug mapping
    const slug = getSlugFromProductId(cellId)
    if (slug) {
      // Use setLocation for SPA navigation (no reload)
      setLocation(`/explorer?cell=${slug}&section=factsheet`)
    } else {
      console.warn('[CompareCellChip] No slug found for cellId:', cellId)
    }
  }

  return (
    <div
      className={`compare-cell-chip ${isHidden ? 'hidden-cell' : ''} ${limitLocked ? 'compare-cell-chip--limit-locked' : ''}`}
      onClick={handleCellClick}
    >
      <div className="chip-image-wrapper">
        {limitLocked && (
          <div
            className="chip-image__lock-overlay"
            aria-label="Deep-dive limit reached — resets soon"
            title="Deep-dive limit reached — resets soon"
          >
            <LockIcon />
          </div>
        )}
        {imageUrl && !imageError ? (
          <img
            src={imageUrl}
            alt={name}
            className="chip-image"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="chip-image-placeholder">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="6" y="2" width="12" height="20" rx="2"></rect>
              <line x1="6" y1="7" x2="18" y2="7"></line>
            </svg>
          </div>
        )}
      </div>

      <div className="chip-content">
        <div className="chip-manufacturer">{manufacturer}</div>
        <div className="chip-name" title={name}>
          {name}
        </div>
        <div className="chip-details">
          <span className="chip-chemistry">{chemistry}</span>
          <span className="chip-separator">·</span>
          <span className="chip-format">{format}</span>
        </div>
        <div className="chip-energy">
          {energyValue > 0 ? energyValue.toFixed(0) : 'N/A'} {energyUnit}
        </div>
        
        {isHidden && (
          <div className="chip-hidden-badge" title="Hidden from plots">
            Hidden
          </div>
        )}
      </div>
      
      {/* Control Buttons */}
      <div className="chip-actions">
        <button 
          className={`chip-visibility-btn ${isHidden ? 'hidden' : 'visible'}`}
          onClick={handleToggleVisibility}
          title={isHidden ? 'Show in plots' : 'Hide from plots'}
        >
          {isHidden ? <EyeOffIcon /> : <EyeIcon />}
        </button>
        
        <button 
          className="chip-remove-btn" 
          onClick={handleRemove}
          title="Remove from comparison"
        >
          <XIcon />
        </button>
      </div>
    </div>
  )
}
